import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "../services/api";
import PageShell from "../components/PageShell";

/** Admin settings — set each topic public or private. */
export default function SettingsPage() {
  const [topics, setTopics] = useState([]);
  const [status, setStatus] = useState("loading");
  const [savingSlug, setSavingSlug] = useState(null);

  useEffect(() => {
    apiService
      .adminListTopics()
      .then((data) => {
        setTopics(data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  const toggle = async (topic) => {
    const next = topic.visibility === "public" ? "private" : "public";
    setSavingSlug(topic.slug);
    try {
      const updated = await apiService.setTopicVisibility(topic.slug, next);
      setTopics((prev) =>
        prev.map((t) =>
          t.slug === topic.slug ? { ...t, visibility: updated.visibility } : t,
        ),
      );
    } catch {
      // leave state as-is on failure
    } finally {
      setSavingSlug(null);
    }
  };

  return (
    <PageShell>
      <Link
        to="/articles"
        className="text-sm text-sky-400 transition hover:text-sky-300"
      >
        ← Back
      </Link>
      <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-100">
        Settings
      </h1>
      <p className="mt-1 text-slate-400">
        Topic visibility. <strong className="text-slate-100">Public</strong>{" "}
        topics can be read by anyone;{" "}
        <strong className="text-slate-100">private</strong> topics require
        sign-in.
      </p>

      {status === "loading" && <p className="mt-6 text-slate-500">Loading…</p>}
      {status === "error" && (
        <p className="mt-6 text-rose-400">Could not load settings.</p>
      )}

      {status === "ready" && (
        <ul className="mt-6 divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/40">
          {topics.map((topic) => {
            const isPublic = topic.visibility === "public";
            return (
              <li
                key={topic.slug}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <div>
                  <div className="font-medium text-slate-100">{topic.name}</div>
                  <div className="text-xs text-slate-500">
                    {topic.count} {topic.count === 1 ? "article" : "articles"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full px-3 py-0.5 text-xs ${
                      isPublic
                        ? "bg-emerald-500/15 text-emerald-400"
                        : "bg-amber-500/15 text-amber-400"
                    }`}
                  >
                    {isPublic ? "🌐 public" : "🔒 private"}
                  </span>
                  <button
                    onClick={() => toggle(topic)}
                    disabled={savingSlug === topic.slug}
                    className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 transition hover:border-sky-500/50 hover:text-sky-300 disabled:opacity-60"
                  >
                    {savingSlug === topic.slug
                      ? "Saving…"
                      : `Make ${isPublic ? "private" : "public"}`}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
}
